import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ShareableScopeKind } from "../../../../shared/rbac/permissions";
import {
  type GrantEntry,
  type RoleOption,
  roleLabel,
  rolesForScope,
} from "../../../../shared/rbac/roles";
import type { SpaceMode } from "../../../../shared/schema";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useMouseDownOutside } from "../../../hooks/useMouseDownOutside";
import { countSpaceMembers } from "../../../lib/countSpaceMembers";
import { defaultRoleForScope } from "../../../lib/defaultRoleForScope";
import { memberCountLabel } from "../../../lib/memberGroupMap";
import {
  grantToRoleChip,
  type ResolveScopeName,
  type RoleChip,
  roleIdFromChipKey,
} from "../../../lib/roleChip";
import { playClick } from "../../../lib/sounds";
import { spaceIcon } from "../../../lib/spaceIcon";
import { Button } from "../../Button";
import { ConnectorLogo } from "../../ConnectorLogo";
import { ConnectorLogoContainer } from "../../ConnectorLogoContainer";
import { Dropdown } from "../../Dropdown";
import { openRoleModal } from "../../globalModalStack";
import { Icon } from "../../Icon";
import { RedactedScopeRow } from "../../RedactedScopeRow";
import { RolePills } from "../../RolePills";
import type { ScopeAccessPendingHandle } from "../../ScopeAccessTable";
import { MetaSep } from "../../MetaSeparator";
import { Text } from "../../Text";
import { inferScopeKind } from "../lib/inferScopeKind";
import { OverflowBadges } from "../OverflowBadges";
import { buildScopeDesired } from "./buildScopeDesired";
import {
  AnchoredListPopover,
  ClickablePillBadge,
  DetailLabel,
  DetailList,
  DetailRow,
  DetailRowRight,
  MatchRow,
  MatchSectionLabel,
  NeutralIconBadge,
  PlainInput,
  SearchBar,
  SearchIconWrap,
  SearchInputWrap,
} from "../shared";

type RoleGrantRow = {
  userId: string | null;
  userGroupId: string | null;
  serviceAccountId: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

type MemberGrant = {
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

type InheritedGrant = {
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
  customRole: { id: string; name: string; scopeKind: string } | null;
  viaGroupId: string;
  viaGroupName: string;
};

type SpaceRow = {
  id: string;
  name: string;
  mode: SpaceMode;
  roleGrantsAsScope: readonly RoleGrantRow[];
};

type AppRow = {
  id: string;
  name: string;
  identifier: string;
  roleGrantsAsScope: readonly RoleGrantRow[];
};

type SkillRow = {
  id: string;
  name: string;
  roleGrantsAsScope: readonly RoleGrantRow[];
};

type CustomRole = { id: string; name: string; scopeKind: string };

type ManageableKind = "space" | "connected_app" | "skill";

type Scope = {
  scopeKind: ManageableKind;
  name: string;
  mode?: SpaceMode;
  identifier?: string;
  grants: readonly RoleGrantRow[];
};

type Row = {
  scopeId: string;
  scopeKind: ManageableKind;
  name: string;
  mode?: SpaceMode;
  identifier?: string;
  directGrants: GrantEntry[];
  directRoleLabels: string[];
  roleOptions: RoleOption[];
  editable: boolean;
  inheritedChips: RoleChip[];
  viaGroups: { id: string; name: string }[];
};

type StagedScope = {
  key: string;
  scopeId: string;
  scopeKind: ManageableKind;
  name: string;
  mode?: SpaceMode;
  identifier?: string;
  roleOptions: RoleOption[];
  roles: RoleOption[];
};

type Match = { scopeId: string; scopeKind: ManageableKind };

const SCOPE_ORDER: Record<ManageableKind, number> = {
  space: 0,
  connected_app: 1,
  skill: 2,
};

const Root = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const SelectedHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const MatchDivider = styled("div")`
  height: 0.5px;
  margin: 12px 8px 4px;
  background: var(--border-alpha);
`;

const MatchIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

function ScopeIcon({
  scopeKind,
  mode,
  name,
  identifier,
}: {
  scopeKind: ManageableKind;
  mode?: SpaceMode;
  name: string;
  identifier?: string;
}) {
  if (scopeKind === "connected_app" && identifier) {
    return (
      <ConnectorLogoContainer
        size={32}
        borderRadius={8}
        outline={false}
        hoverBackground={false}
      >
        <ConnectorLogo template={{ name, identifier }} size={16} />
      </ConnectorLogoContainer>
    );
  }
  if (scopeKind === "skill") return <NeutralIconBadge iconName="skills" />;
  return <NeutralIconBadge iconName={mode ? spaceIcon(mode) : "hash"} />;
}

function scopeKindLabel(scopeKind: ManageableKind): string {
  return scopeKind === "space"
    ? "Space"
    : scopeKind === "skill"
      ? "Skill"
      : "Connector";
}

function viaLabel(viaGroups: readonly { name: string }[]): string {
  if (viaGroups.length === 1) return `via ${viaGroups[0]!.name}`;
  return `via ${viaGroups.length} groups`;
}

export function MemberAccessTable({
  memberId,
  isSelf,
  memberDirectGrants,
  inheritedGrants,
  spaces,
  apps,
  skills,
  customRoles,
  resolveScopeName,
  personalSpaceId,
  manageableSpaceIds,
  manageableAppIds,
  manageableSkillIds,
  canManage,
  stagedGrantsRef,
  hasTenantRead,
  onOpenResource,
  commitScope,
  onRequestSelfRemove,
  onRemoveConnectorAccess,
}: {
  memberId: string;
  isSelf: boolean;
  memberDirectGrants: readonly MemberGrant[];
  inheritedGrants: readonly InheritedGrant[];
  spaces: readonly SpaceRow[];
  apps: readonly AppRow[];
  skills: readonly SkillRow[];
  customRoles: readonly CustomRole[];
  resolveScopeName: ResolveScopeName;
  personalSpaceId: string | undefined;
  manageableSpaceIds: ReadonlySet<string>;
  manageableAppIds: ReadonlySet<string>;
  manageableSkillIds: ReadonlySet<string>;
  canManage: boolean;
  stagedGrantsRef?: { current: ScopeAccessPendingHandle | null };
  hasTenantRead: { space: boolean; connected_app: boolean; skill: boolean };
  onOpenResource: (scopeId: string, scopeKind: ShareableScopeKind) => void;
  commitScope: (
    scopeId: string,
    scopeKind: ShareableScopeKind,
    desired: GrantEntry[],
    onCommitted?: () => void
  ) => boolean | void;
  onRequestSelfRemove: (
    scopeId: string,
    scopeKind: ShareableScopeKind,
    desired: GrantEntry[]
  ) => void;
  onRemoveConnectorAccess: (appId: string, desired: GrantEntry[]) => void;
}) {
  const { memberUserIds } = useGroupMemberships();

  const [search, setSearch] = useState("");
  const [showMatches, setShowMatches] = useState(false);
  const [staged, setStaged] = useState<StagedScope[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const [searchBarRef, popoverRef] = useMouseDownOutside(() =>
    setShowMatches(false)
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const matchRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const rolesMap = useMemo(
    () => new Map(customRoles.map((r) => [r.id, r])),
    [customRoles]
  );
  const roleOptionsFor = useCallback(
    (kind: ManageableKind) => rolesForScope(kind, customRoles as CustomRole[]),
    [customRoles]
  );

  const scopeById = useMemo(() => {
    const m = new Map<string, Scope>();
    for (const s of spaces)
      m.set(s.id, {
        scopeKind: "space",
        name: s.name,
        mode: s.mode,
        grants: s.roleGrantsAsScope,
      });
    for (const a of apps)
      m.set(a.id, {
        scopeKind: "connected_app",
        name: a.name,
        identifier: a.identifier,
        grants: a.roleGrantsAsScope,
      });
    for (const sk of skills)
      m.set(sk.id, {
        scopeKind: "skill",
        name: sk.name,
        grants: sk.roleGrantsAsScope,
      });
    return m;
  }, [spaces, apps, skills]);

  const directScopeIds = useMemo(
    () => new Set(memberDirectGrants.map((g) => g.scopeId)),
    [memberDirectGrants]
  );

  const { rows, hidden } = useMemo(() => {
    const directByScope = new Map<string, MemberGrant[]>();
    for (const g of memberDirectGrants) {
      const arr = directByScope.get(g.scopeId);
      if (arr) arr.push(g);
      else directByScope.set(g.scopeId, [g]);
    }
    const inheritedByScope = new Map<string, InheritedGrant[]>();
    for (const g of inheritedGrants) {
      const arr = inheritedByScope.get(g.scopeId);
      if (arr) arr.push(g);
      else inheritedByScope.set(g.scopeId, [g]);
    }
    const manageableFor = (kind: ManageableKind) =>
      kind === "space"
        ? manageableSpaceIds
        : kind === "connected_app"
          ? manageableAppIds
          : manageableSkillIds;

    const rows: Row[] = [];
    const hidden = { space: 0, connected_app: 0, skill: 0 };
    const scopeIds = new Set([
      ...directByScope.keys(),
      ...inheritedByScope.keys(),
    ]);
    for (const scopeId of scopeIds) {
      if (scopeId === personalSpaceId) continue;
      const direct = directByScope.get(scopeId) ?? [];
      const inherited = inheritedByScope.get(scopeId) ?? [];
      const sample = direct[0] ?? inherited[0];
      if (!sample) continue;
      const kind = inferScopeKind(sample, rolesMap);
      if (kind !== "space" && kind !== "connected_app" && kind !== "skill")
        continue;
      const scope = scopeById.get(scopeId);
      if (scope?.mode === "personal") continue;
      const name = scope ? resolveScopeName(scopeId, kind) : null;
      if (!scope || name == null) {
        hidden[kind]++;
        continue;
      }
      const directGrants: GrantEntry[] = direct.map((g) => ({
        userId: memberId,
        builtinRoleId: g.builtinRoleId ?? undefined,
        customRoleId: g.customRoleId ?? undefined,
      }));
      const chipByKey = new Map<string, RoleChip>();
      const viaGroups = new Map<string, string>();
      for (const g of inherited) {
        const chip = grantToRoleChip({
          builtinRoleId: g.builtinRoleId,
          customRole: g.customRole,
        });
        if (chip && !chipByKey.has(chip.key)) chipByKey.set(chip.key, chip);
        viaGroups.set(g.viaGroupId, g.viaGroupName);
      }
      rows.push({
        scopeId,
        scopeKind: kind,
        name,
        mode: scope.mode,
        identifier: scope.identifier,
        directGrants,
        directRoleLabels: directGrants
          .map((g) => roleLabel(g, customRoles as CustomRole[]))
          .sort(),
        roleOptions: roleOptionsFor(kind),
        editable: canManage && manageableFor(kind).has(scopeId),
        inheritedChips: [...chipByKey.values()],
        viaGroups: [...viaGroups].map(([id, name]) => ({ id, name })),
      });
    }
    rows.sort((a, b) => {
      const kindDiff = SCOPE_ORDER[a.scopeKind] - SCOPE_ORDER[b.scopeKind];
      if (kindDiff !== 0) return kindDiff;
      return a.name.localeCompare(b.name);
    });
    return { rows, hidden };
  }, [
    memberDirectGrants,
    inheritedGrants,
    rolesMap,
    scopeById,
    resolveScopeName,
    memberId,
    personalSpaceId,
    customRoles,
    roleOptionsFor,
    manageableSpaceIds,
    manageableAppIds,
    manageableSkillIds,
    canManage,
  ]);

  const visibleHidden = {
    space: hasTenantRead.space ? 0 : hidden.space,
    connected_app: hasTenantRead.connected_app ? 0 : hidden.connected_app,
    skill: hasTenantRead.skill ? 0 : hidden.skill,
  };

  const stagedKeys = useMemo(() => new Set(staged.map((s) => s.key)), [staged]);

  const { spaceMatches, appMatches, skillMatches, flatMatches } =
    useMemo(() => {
      const lower = search.trim().toLowerCase();
      const matchesQuery = (label: string) =>
        !lower || label.toLowerCase().includes(lower);
      const spaceMatches = spaces.filter(
        (s) =>
          manageableSpaceIds.has(s.id) &&
          s.mode !== "personal" &&
          s.id !== personalSpaceId &&
          !directScopeIds.has(s.id) &&
          !stagedKeys.has(`space:${s.id}`) &&
          matchesQuery(s.name)
      );
      const appMatches = apps.filter(
        (a) =>
          manageableAppIds.has(a.id) &&
          !directScopeIds.has(a.id) &&
          !stagedKeys.has(`connected_app:${a.id}`) &&
          matchesQuery(a.name)
      );
      const skillMatches = skills.filter(
        (sk) =>
          manageableSkillIds.has(sk.id) &&
          !directScopeIds.has(sk.id) &&
          !stagedKeys.has(`skill:${sk.id}`) &&
          matchesQuery(sk.name)
      );
      const flatMatches: Match[] = [
        ...spaceMatches.map((s) => ({
          scopeId: s.id,
          scopeKind: "space" as const,
        })),
        ...appMatches.map((a) => ({
          scopeId: a.id,
          scopeKind: "connected_app" as const,
        })),
        ...skillMatches.map((sk) => ({
          scopeId: sk.id,
          scopeKind: "skill" as const,
        })),
      ];
      return { spaceMatches, appMatches, skillMatches, flatMatches };
    }, [
      search,
      spaces,
      apps,
      skills,
      manageableSpaceIds,
      manageableAppIds,
      manageableSkillIds,
      personalSpaceId,
      directScopeIds,
      stagedKeys,
    ]);

  const hasMatches = flatMatches.length > 0;

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- reset highlight when query changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  useEffect(() => {
    if (!showMatches) return;
    matchRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, showMatches]);

  const selectMatch = useCallback(
    (match: Match) => {
      const scope = scopeById.get(match.scopeId);
      if (!scope) return;
      playClick();
      const roleOptions = roleOptionsFor(match.scopeKind);
      const def = defaultRoleForScope(match.scopeKind, roleOptions);
      const key = `${match.scopeKind}:${match.scopeId}`;
      setStaged((prev) =>
        prev.some((s) => s.key === key)
          ? prev
          : [
              ...prev,
              {
                key,
                scopeId: match.scopeId,
                scopeKind: match.scopeKind,
                name: scope.name,
                mode: scope.mode,
                identifier: scope.identifier,
                roleOptions,
                roles: def ? [def] : [],
              },
            ]
      );
      setSearch("");
      setHighlightedIndex(0);
      setShowMatches(false);
      inputRef.current?.blur();
    },
    [scopeById, roleOptionsFor]
  );

  const toggleStagedRole = useCallback((key: string, opt: RoleOption) => {
    setStaged((prev) =>
      prev.map((s) => {
        if (s.key !== key) return s;
        const has = s.roles.some((r) => sameRole(r, opt));
        if (has) {
          if (s.roles.length <= 1) return s;
          return { ...s, roles: s.roles.filter((r) => !sameRole(r, opt)) };
        }
        return { ...s, roles: [...s.roles, opt] };
      })
    );
  }, []);

  const removeStaged = useCallback((key: string) => {
    setStaged((prev) => prev.filter((s) => s.key !== key));
  }, []);

  // Once the member actually holds a staged scope's roles — e.g. after a
  // deferred grant modal confirms the add — drop it from the staged list.
  useEffect(() => {
    setStaged((prev) => {
      const next = prev.filter((s) => {
        const scope = scopeById.get(s.scopeId);
        if (!scope) return true;
        const held = scope.grants.filter((g) => g.userId === memberId);
        return s.roles.some(
          (role) =>
            !held.some(
              (g) =>
                (role.builtinRoleId != null &&
                  g.builtinRoleId === role.builtinRoleId) ||
                (role.customRoleId != null &&
                  g.customRoleId === role.customRoleId)
            )
        );
      });
      return next.length === prev.length ? prev : next;
    });
  }, [scopeById, memberId]);

  const commitAll = (onCommitted?: () => void) => {
    if (staged.length === 0) return;
    // A deferred space-access grant modal keeps its scope staged until confirmed
    // and runs onCommitted itself; otherwise everything commits now.
    const remaining: StagedScope[] = [];
    let anyDeferred = false;
    for (const s of staged) {
      const scope = scopeById.get(s.scopeId);
      if (!scope) continue;
      const deferred =
        commitScope(
          s.scopeId,
          s.scopeKind,
          buildScopeDesired(scope.grants, memberId, s.roles),
          onCommitted
        ) === false;
      if (deferred) {
        anyDeferred = true;
        remaining.push(s);
      }
    }
    setStaged(remaining);
    setSearch("");
    setShowMatches(false);
    if (!anyDeferred) onCommitted?.();
  };

  useEffect(() => {
    if (!stagedGrantsRef) return;
    stagedGrantsRef.current = {
      pendingCount: staged.length,
      canCommit: staged.length > 0 && staged.every((s) => s.roles.length > 0),
      commitPending: commitAll,
    };
    return () => {
      stagedGrantsRef.current = null;
    };
  });

  const toggleRow = useCallback(
    (row: Row, opt: RoleOption) => {
      const current: RoleOption[] = row.directGrants.map((g) => ({
        builtinRoleId: g.builtinRoleId as RoleOption["builtinRoleId"],
        customRoleId: g.customRoleId,
        label: "",
      }));
      const has = current.some((r) => sameRole(r, opt));
      if (has && current.length <= 1) return;
      const next = has
        ? current.filter((r) => !sameRole(r, opt))
        : [...current, opt];
      const scope = scopeById.get(row.scopeId);
      if (!scope) return;
      commitScope(
        row.scopeId,
        row.scopeKind,
        buildScopeDesired(scope.grants, memberId, next)
      );
    },
    [scopeById, commitScope, memberId]
  );

  const removeRow = useCallback(
    (row: Row) => {
      const scope = scopeById.get(row.scopeId);
      if (!scope) return;
      const desired = buildScopeDesired(scope.grants, memberId, []);
      if (row.scopeKind === "connected_app")
        onRemoveConnectorAccess(row.scopeId, desired);
      else if (isSelf) onRequestSelfRemove(row.scopeId, row.scopeKind, desired);
      else commitScope(row.scopeId, row.scopeKind, desired);
    },
    [
      scopeById,
      memberId,
      isSelf,
      onRequestSelfRemove,
      commitScope,
      onRemoveConnectorAccess,
    ]
  );

  const isEmpty =
    rows.length === 0 &&
    staged.length === 0 &&
    visibleHidden.space === 0 &&
    visibleHidden.connected_app === 0 &&
    visibleHidden.skill === 0;

  return (
    <Root>
      {canManage && (
        <SearchBar
          ref={searchBarRef}
          onMouseDown={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("button, input, [data-no-focus-steal]")) return;
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <SearchInputWrap>
            <SearchIconWrap>
              <Icon name="search" size={16} />
            </SearchIconWrap>
            <PlainInput
              ref={inputRef}
              placeholder="Add a space, connector, or skill…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowMatches(true);
              }}
              onFocus={() => setShowMatches(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setShowMatches(false);
                  setSearch("");
                } else if (e.key === "ArrowDown" && hasMatches) {
                  e.preventDefault();
                  setShowMatches(true);
                  setHighlightedIndex((i) =>
                    Math.min(i + 1, flatMatches.length - 1)
                  );
                } else if (e.key === "ArrowUp" && hasMatches) {
                  e.preventDefault();
                  setShowMatches(true);
                  setHighlightedIndex((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter" && hasMatches && showMatches) {
                  e.preventDefault();
                  const match = flatMatches[highlightedIndex];
                  if (match) selectMatch(match);
                }
              }}
            />
          </SearchInputWrap>
          <AnchoredListPopover
            anchorRef={searchBarRef}
            open={showMatches && hasMatches}
            popoverRef={popoverRef}
          >
            {spaceMatches.length > 0 && (
              <>
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Spaces
                  </Text>
                </MatchSectionLabel>
                {spaceMatches.map((s, i) => (
                  <MatchRow
                    key={`space:${s.id}`}
                    ref={(el) => {
                      matchRefs.current[i] = el;
                    }}
                    type="button"
                    data-highlighted={highlightedIndex === i}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectMatch({ scopeId: s.id, scopeKind: "space" });
                    }}
                  >
                    <MatchIcon>
                      <Icon name={spaceIcon(s.mode)} size={16} />
                    </MatchIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        variant="text-regular"
                        color="var(--text-primary)"
                        ellipsis
                      >
                        {s.name}
                      </Text>
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {memberCountLabel(
                          countSpaceMembers(s.roleGrantsAsScope, memberUserIds)
                        )}
                      </Text>
                    </div>
                  </MatchRow>
                ))}
              </>
            )}
            {appMatches.length > 0 && (
              <>
                {spaceMatches.length > 0 && <MatchDivider />}
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Connectors
                  </Text>
                </MatchSectionLabel>
                {appMatches.map((a, i) => {
                  const flatIndex = spaceMatches.length + i;
                  return (
                    <MatchRow
                      key={`connected_app:${a.id}`}
                      ref={(el) => {
                        matchRefs.current[flatIndex] = el;
                      }}
                      type="button"
                      data-highlighted={highlightedIndex === flatIndex}
                      onMouseEnter={() => setHighlightedIndex(flatIndex)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectMatch({
                          scopeId: a.id,
                          scopeKind: "connected_app",
                        });
                      }}
                    >
                      <MatchIcon>
                        <ConnectorLogo
                          template={{ name: a.name, identifier: a.identifier }}
                          size={16}
                        />
                      </MatchIcon>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {a.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {memberCountLabel(
                            countSpaceMembers(
                              a.roleGrantsAsScope,
                              memberUserIds
                            )
                          )}
                        </Text>
                      </div>
                    </MatchRow>
                  );
                })}
              </>
            )}
            {skillMatches.length > 0 && (
              <>
                {(spaceMatches.length > 0 || appMatches.length > 0) && (
                  <MatchDivider />
                )}
                <MatchSectionLabel>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Skills
                  </Text>
                </MatchSectionLabel>
                {skillMatches.map((sk, i) => {
                  const flatIndex = spaceMatches.length + appMatches.length + i;
                  return (
                    <MatchRow
                      key={`skill:${sk.id}`}
                      ref={(el) => {
                        matchRefs.current[flatIndex] = el;
                      }}
                      type="button"
                      data-highlighted={highlightedIndex === flatIndex}
                      onMouseEnter={() => setHighlightedIndex(flatIndex)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectMatch({ scopeId: sk.id, scopeKind: "skill" });
                      }}
                    >
                      <MatchIcon>
                        <Icon name="skills" size={16} />
                      </MatchIcon>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                          ellipsis
                        >
                          {sk.name}
                        </Text>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {memberCountLabel(
                            countSpaceMembers(
                              sk.roleGrantsAsScope,
                              memberUserIds
                            )
                          )}
                        </Text>
                      </div>
                    </MatchRow>
                  );
                })}
              </>
            )}
          </AnchoredListPopover>
        </SearchBar>
      )}

      {staged.length > 0 && (
        <DetailList>
          <SelectedHeader>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Selected
            </Text>
            <Button
              variant="primary"
              disabled={staged.some((s) => s.roles.length === 0)}
              onClick={() => commitAll()}
            >
              Add {staged.length}
            </Button>
          </SelectedHeader>
          {staged.map((s) => (
            <DetailRow key={s.key}>
              <ScopeIcon
                scopeKind={s.scopeKind}
                mode={s.mode}
                name={s.name}
                identifier={s.identifier}
              />
              <DetailLabel>
                <Text
                  variant="text-regular-md"
                  color="var(--text-primary)"
                  ellipsis
                >
                  {s.name}
                </Text>
                <Text variant="text-small" color="var(--text-secondary)">
                  {scopeKindLabel(s.scopeKind)}
                </Text>
              </DetailLabel>
              <DetailRowRight>
                <RolePills
                  roles={s.roles.map((r) => r.label).sort()}
                  grants={s.roles.map((r) => ({
                    builtinRoleId: r.builtinRoleId,
                    customRoleId: r.customRoleId,
                  }))}
                  roleOptions={s.roleOptions}
                  canManage
                  onToggle={(opt) => toggleStagedRole(s.key, opt)}
                />
                <Button
                  variant="secondary"
                  icon="x"
                  onClick={() => removeStaged(s.key)}
                />
              </DetailRowRight>
            </DetailRow>
          ))}
        </DetailList>
      )}

      <DetailList>
        {isEmpty ? (
          <DetailRow>
            <NeutralIconBadge iconName="circle-slash" />
            <DetailLabel>
              <Text variant="text-regular-md">No access</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Access appears here when this member or one of their groups is
                granted a role.
              </Text>
            </DetailLabel>
          </DetailRow>
        ) : (
          <>
            {rows.map((row) => (
              <DetailRow
                key={row.scopeId}
                data-interactive="true"
                onMouseDown={() => playClick()}
                onClick={() => onOpenResource(row.scopeId, row.scopeKind)}
              >
                <ScopeIcon
                  scopeKind={row.scopeKind}
                  mode={row.mode}
                  name={row.name}
                  identifier={row.identifier}
                />
                <DetailLabel>
                  <Text
                    variant="text-regular-md"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {row.name}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {scopeKindLabel(row.scopeKind)}
                    {row.viaGroups.length > 0 && (
                      <>
                        <MetaSep />
                        {viaLabel(row.viaGroups)}
                      </>
                    )}
                  </Text>
                </DetailLabel>
                <DetailRowRight>
                  {row.directGrants.length > 0 ? (
                    <>
                      <RolePills
                        roles={row.directRoleLabels}
                        grants={row.directGrants}
                        roleOptions={row.roleOptions}
                        canManage={row.editable}
                        onToggle={(opt) => toggleRow(row, opt)}
                      />
                      {row.editable && (
                        <div
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Dropdown
                            opener={<Button variant="secondary" icon="more" />}
                            align="right"
                            minWidth={160}
                            sections={[
                              {
                                items: [
                                  {
                                    type: "button" as const,
                                    left: <Icon name="trash" size={16} />,
                                    label: "Remove access",
                                    onClick: () => removeRow(row),
                                  },
                                ],
                              },
                            ]}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <OverflowBadges
                      items={row.inheritedChips}
                      keyFn={(chip) => chip.key}
                      renderBadge={(chip) => (
                        <ClickablePillBadge
                          icon={chip.icon}
                          onClick={() => {
                            playClick();
                            openRoleModal(roleIdFromChipKey(chip.key));
                          }}
                        >
                          {chip.label}
                        </ClickablePillBadge>
                      )}
                      overflowTooltip={(h) => h.map((c) => c.label).join(", ")}
                    />
                  )}
                </DetailRowRight>
              </DetailRow>
            ))}
            <RedactedScopeRow count={visibleHidden.space} scopeKind="space" />
            <RedactedScopeRow
              count={visibleHidden.connected_app}
              scopeKind="connected_app"
            />
            <RedactedScopeRow count={visibleHidden.skill} scopeKind="skill" />
          </>
        )}
      </DetailList>
    </Root>
  );
}

function sameRole(
  a: { builtinRoleId?: string; customRoleId?: string },
  b: { builtinRoleId?: string; customRoleId?: string }
): boolean {
  return (
    (!!a.builtinRoleId && a.builtinRoleId === b.builtinRoleId) ||
    (!!a.customRoleId && a.customRoleId === b.customRoleId)
  );
}
